import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase/supabase';
import type { User as UserData, Role } from '@shiftly/shared/src/schema';
import { useUser } from '../lib/supabase/users/supabaseUserService';

interface AuthContextType {
  user: User | null;
  userData: UserData | null;
  session: Session | null;
  loading: boolean;
  // signUp: (email: string, password: string, full_name: string) => Promise<{ error: Error | null }>;
  roles: string[];
  permissions: string[];
  refetchUserData: () => Promise<void>;
  migratePassword: (email: string, password: string) => Promise<{ data: any, error: string | null }>;
  signIn: (email: string, password: string) => Promise<{ data: any, error: string | null }>;
  checkUserPasswordStatus: (email: string) => Promise<{ hasPassword: boolean; error: string | null }>;
  logout: () => Promise<void>;
  sendConfirmationEmail: (email: string) => Promise<{ success: boolean; error: string | null }>;
  sendPasswordReset: (email: string) => Promise<{ success: boolean; error: string | null }>;
  emulatedRole: Role | null;
  setEmulatedRole: (role: Role | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [emulatedRole, setEmulatedRole] = useState<Role | null>(() => {
    if (process.env.NODE_ENV !== 'development') {
      return null;
    }
    const storedRole = localStorage.getItem('emulatedRole');
    return storedRole ? JSON.parse(storedRole) : null;
  });

  const { data: userData, isLoading: userLoading, refetch } = useUser(
    currentUser?.id ?? ''
  );

  useEffect(() => {
    if (emulatedRole) {
      localStorage.setItem('emulatedRole', JSON.stringify(emulatedRole));
    } else {
      localStorage.removeItem('emulatedRole');
    }
  }, [emulatedRole]);

  useEffect(() => {
    supabase.auth.refreshSession().then(({ data: { session } }) => {
      setSession(session);
      setCurrentUser(session?.user ?? null);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        setCurrentUser(session?.user ?? null);
        setLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  /* If we want instantaneous permission updates, re-enable this & set the tables to realtime, I'm not sure if it's necessary
  
  // Real-time permission monitoring and JWT refresh
  useEffect(() => {
    if (!currentUser?.id) return;

    let refreshTimeout: NodeJS.Timeout;

    const handlePermissionChange = (): void => {
      // Debounce rapid changes
      clearTimeout(refreshTimeout);
      refreshTimeout = setTimeout(async () => {
        try {
          console.log('Permissions changed, refreshing session...');
          await supabase.auth.refreshSession();
        } catch (error) {
          console.error('Failed to refresh session after permission change:', error);
        }
      }, 1000);
    };

    // Subscribe to user_roles changes (roles added/removed from user)
    const userRolesChannel = supabase
      .channel('user-roles-changes', { config: { private: true } })
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'user_roles',
        filter: `user_id=eq.${currentUser.id}`
      }, handlePermissionChange)
      .subscribe();

    // Subscribe to role_permissions changes for user's current roles
    let rolePermissionsChannel: any = null;

    const subscribeToRolePermissions = () => {
      const userRoles: string[] = session?.user?.user_metadata?.roles || [];

      if (userRoles.length > 0) {
        rolePermissionsChannel = supabase
          .channel('role-permissions-changes', { config: { private: true } })
          .on('postgres_changes', {
            event: '*',
            schema: 'public',
            table: 'role_permissions',
            filter: `role_id=in.(${userRoles.join(',')})`
          }, handlePermissionChange)
          .subscribe();
      }
    };

    subscribeToRolePermissions();

    return () => {
      clearTimeout(refreshTimeout);
      userRolesChannel.unsubscribe();
      if (rolePermissionsChannel) {
        rolePermissionsChannel.unsubscribe();
      }
    };
  }, [currentUser?.id, session?.user?.user_metadata?.roles]);*/

  useEffect(() => {
    const userRoles: string[] = session?.user?.user_metadata?.roles || [];
    if (!emulatedRole) {
      fetchPermissionsByRoles(userRoles);
    } else {
      setPermissions(emulatedRole.permissions);
    }
    setRoles(userRoles);
  }, [session?.user?.user_metadata?.roles, emulatedRole]);

  const fetchPermissionsByRoles = async (roleIds: string[]) => {
    if (!roleIds.length) {
      setPermissions([]);
      return [];
    }

    try {
      const { data, error } = await supabase
        .from('role_permissions')
        .select(`
          permissions!inner (
            key
          )
        `)
        .in('role_id', roleIds);

      if (error) {
        console.error('Error fetching permissions:', error);
        return [];
      }

      const permissionKeys = data?.map((rp: any) => rp.permissions.key) || [];
      const uniquePermissions = [...new Set(permissionKeys)];
      setPermissions(uniquePermissions);
      return uniquePermissions;
    } catch (error) {
      console.error('Error fetching permissions:', error);
      return [];
    }
  };

  // const signUp = async (email: string, password: string, full_name: string) => {
  //   const { error } = await supabase.auth.signUp({
  //     email,
  //     password,
  //     options: {
  //       data: {
  //         full_name: full_name
  //       }
  //     }
  //   });
  //   return { error };
  // };

  const signIn = async (email: string, password: string) => {
    try {
      console.log('Attempting sign in with:', email);
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });

      if (error) {
        console.error('Supabase sign in error:', error);

        let errorMessage = error.message;
        if (error.message.includes('Invalid login credentials')) {
          errorMessage = 'Invalid email or password. Please check your credentials and try again.';
        } else if (error.message.includes('Email not confirmed')) {
          try {
            await sendConfirmationEmail(email);
          } catch (emailError) {
            console.error('Failed to send migration confirmation email:', emailError);
          }
          
          errorMessage = 'Please check your email and confirm your account before signing in.';
        } else if (error.message.includes('Too many requests')) {
          errorMessage = 'Too many login attempts. Please wait a moment before trying again.';
        }
        return { data: null, error: errorMessage };
      }

      return { data, error: null };
    } catch (err: any) {
      console.error('Unexpected error during sign in:', err);
      return { data: null, error: err.message || 'An unexpected error occurred during sign in' };
    }
  };

  const checkUserPasswordStatus = async (email: string) => {
    try {
      console.log('Checking password status for:', email);
      const { data, error } = await supabase.functions.invoke('user-password-status', {
        body: {
          email: email
        }
      });

      if (error) {
        console.error('Check password status function error:', error);
        if (error.message.includes('Function not found') || error.message.includes('404')) {
          return { hasPassword: false, error: 'Password status check service is not available. Please contact support.' };
        }
        return { hasPassword: false, error: error.message };
      }

      console.log('Password status check response:', data);
      return { hasPassword: data?.hasPassword || false, error: null };
    } catch (err: any) {
      console.error('Unexpected error during password status check:', err);
      return { hasPassword: false, error: err.message || 'An unexpected error occurred during password status check' };
    }
  };

  const migratePassword = async (email: string, password: string) => {
    try {
      console.log('Attempting password migration for:', email);
      const { data, error } = await supabase.functions.invoke('verify-password', {
        body: {
          email: email,
          password: password
        }
      });

      if (error) {
        console.error('Migration function error:', error);
        if (error.message.includes('Function not found') || error.message.includes('404')) {
          return { data: null, error: 'Password migration service is not available. Please contact support.' };
        }
        return { data: null, error: error.message };
      }

      // Parse the response if it's a string
      let parsedData = data;
      if (typeof data === 'string') {
        try {
          parsedData = JSON.parse(data);
        } catch (parseError) {
          console.error('Failed to parse migration response:', parseError);
          return { data: null, error: 'Invalid response format from migration service' };
        }
      }
      
      return { data: parsedData, error: null };
    } catch (err: any) {
      console.error('Unexpected error during password migration:', err);
      return { data: null, error: err.message || 'An unexpected error occurred during password migration' };
    }
  };

  const logout = async () => {
    setEmulatedRole(null);
    await supabase.auth.signOut();
  };

  const sendConfirmationEmail = async (email: string) => {
    try {
      const { error } = await supabase.functions.invoke('send-migration-confirmation', {
        body: {
          email: email
        }
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, error: null };
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to send migration confirmation email' };
    }
  };

  const sendPasswordReset = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/password-reset`
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, error: null };
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to send password reset email' };
    }
  };

  const refetchUserData = async () => {
    if (currentUser) {
      try {
        await refetch();
        const userRoles: string[] = session?.user?.user_metadata?.roles || [];
        await fetchPermissionsByRoles(userRoles);
      } catch (error: any) {
        console.error('Failed to refetch user data:', error);
      }
    }
  };

  const value = {
    user: currentUser,
    userData: userData || null,
    session,
    loading: loading || userLoading,
    permissions,
    roles,
    refetchUserData,
    // signUp,
    migratePassword,
    signIn,
    checkUserPasswordStatus,
    logout,
    sendConfirmationEmail,
    sendPasswordReset,
    emulatedRole,
    setEmulatedRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
